import React, { useContext, useEffect, useState } from "react";
import {
  TextInput,
  TouchableOpacity,
  StyleSheet,
  useColorScheme,
  Dimensions,
  Platform,
} from "react-native";
import { Text, View } from "../../../components/shared/Themed";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useToast } from "react-native-toast-notifications";
import { Picker } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { useForm, Controller } from "react-hook-form";
import { addHubspotCompany } from "../../../services/hubspotService";

type Props = {
  navigation: any;
};

type ValidationErrors = {
  [key: string]: string;
};

const { width, height } = Dimensions.get("window");

export default function CreateCompanyForm({ navigation }: Props) {
  const colorScheme = useColorScheme();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const [loading, setLoading] = useState(false);

  // New form state for inputs
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();
  const toast = useToast();

  // Function to simulate form submission
  const submitHandler = async (data: any) => {
    const {
      name,
      domain,
      city,
      industry,
      phone,
      state,
      country,
      website,
      zip,
      address,
    } = data;

    const formData = {
      name,
      domain,
      city,
      industry,
      phone,
      state,
      country,
      website,
      zip,
      address,
    };

    await addHubspotCompany(formData)
      .then((result) => {
        if (result.isSuccessfull) {
          toast.show("Created company successfully", {
            type: "success",
          });
          navigation.goBack();
          // navigation.reset({
          //   index: 0,
          //   routes: [
          //     { name: "HubspotSettings", params: { screen: "Company" } },
          //   ],
          // });
        } else {
          toast.show(result.data, {
            type: "danger",
          });
        }
      })
      .catch((err) => {
        toast.show(err.message, {
          type: "danger",
        });
      });
  };
  const industryOptions = [
    { label: "Agriculture", value: "Agriculture" },
    { label: "Airlines/Aviation", value: "AIRLINES_AVIATION" },
    { label: "Apparel", value: "Apparel" },
    { label: "Automotive", value: "AUTOMOTIVE" },
    { label: "Banking", value: "BANKING" },
  ];
  // Error message clearing after 5 seconds
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        clearErrors(); // Clear the errors after 5 seconds
      }, 5000);
      return () => clearTimeout(timer); // Cleanup timer on unmount or errors change
    }
  }, [errors, clearErrors]);

  return (
    <ScrollView
      style={{
        height: "100%",
        backgroundColor: colorScheme === "light" ? "#fafafa" : "#fafafa",
      }}
    >
      <View style={styles.container}>
        <View style={styles.box}>
          <Text style={styles.header}>Create Company Form</Text>

          <Text style={styles.label}>Company Name *</Text>
          <Controller
            name="name"
            control={control}
            rules={{ required: "Company name is required." }}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.name && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter company name"
              />
            )}
          />
          {errors.name && (
            <Text style={styles.errorText}>{errors.name.message}</Text>
          )}

          <Text style={styles.label}>Domain *</Text>
          <Controller
            name="domain"
            control={control}
            rules={{ required: "Domain is required." }}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.domain && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter domain"
              />
            )}
          />
          {errors.domain && (
            <Text style={styles.errorText}>{errors.domain.message}</Text>
          )}

          <Text style={styles.label}>City</Text>
          <Controller
            name="city"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.city && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter city"
              />
            )}
          />
          {errors.city && (
            <Text style={styles.errorText}>{errors.city?.message}</Text>
          )}

          <Text style={styles.label}>Industry *</Text>
          <Controller
            name="industry"
            control={control}
            rules={{ required: "Industry is required." }}
            render={({ field: { onChange, value } }) => (
              <Picker
                selectedValue={value}
                style={[styles.picker, errors.industry && styles.inputError]}
                onValueChange={onChange}
              >
                <Picker.Item label="Select an industry" value="" />
                {industryOptions.map((option) => (
                  <Picker.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </Picker>
            )}
          />
          {errors.industry && (
            <Text style={styles.errorText}>{errors.industry.message}</Text>
          )}

          <Text style={styles.label}>Phone</Text>
          <Controller
            name="phone"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.phone && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter phone number"
              />
            )}
          />
          {errors.phone && (
            <Text style={styles.errorText}>{errors.phone?.message}</Text>
          )}

          <Text style={styles.label}>State</Text>
          <Controller
            name="state"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.state && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter state"
              />
            )}
          />
          {errors.state && (
            <Text style={styles.errorText}>{errors.state?.message}</Text>
          )}

          <Text style={styles.label}>Country *</Text>
          <Controller
            name="country"
            control={control}
            rules={{ required: "Country is required." }}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.country && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter country"
              />
            )}
          />
          {errors.country && (
            <Text style={styles.errorText}>{errors.country.message}</Text>
          )}

          <Text style={styles.label}>Website</Text>
          <Controller
            name="website"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.website && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter website"
              />
            )}
          />
          {errors.website && (
            <Text style={styles.errorText}>{errors.website?.message}</Text>
          )}

          <Text style={styles.label}>ZIP</Text>
          <Controller
            name="zip"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.zip && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter ZIP"
              />
            )}
          />
          {errors.zip && (
            <Text style={styles.errorText}>{errors.zip?.message}</Text>
          )}

          <Text style={styles.label}>Address</Text>
          <Controller
            name="address"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.input, errors.address && styles.inputError]}
                onChangeText={onChange}
                value={value}
                placeholder="Enter address"
              />
            )}
          />
          {errors.address && (
            <Text style={styles.errorText}>{errors.address?.message}</Text>
          )}

          <TouchableOpacity
            style={styles.button}
            onPress={handleSubmit(submitHandler)}
          >
            <Text style={styles.buttonText}>Submit</Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: width * 0.05,
    paddingVertical: height * 0.02,
    backgroundColor: "#f8f9fa",
    width: "100%",
    marginTop: 50,
    marginBottom: 50,
  },
  box: {
    width: "100%",
    maxWidth: 800,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
    color: "#333",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
    color: "#555",
  },
  input: {
    width: "100%",
    height: 50,
    backgroundColor: "#fff",
    paddingHorizontal: 16,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 16,
    color: "#333",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  inputError: {
    borderColor: "red",
    backgroundColor: "#ffe6e6",
  },
  errorText: {
    color: "red",
    fontSize: 14,
    marginBottom: 8,
    textAlign: "left",
  },
  button: {
    alignItems: "center",
    justifyContent: "center",
    height: 50,
    backgroundColor: "#1E90FF",
    borderRadius: 8,
    marginTop: 20,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 18,
  },
  picker: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    fontSize: 15,
    marginTop: 10,
    marginBottom: 10,
  },
});
